<template>
  <div class="offer-parameters-details">
    <div class="offers-parameters-header">
      <div class="custom-card-header">
        <span>{{ $t('alert.parameters') }}</span>
      </div>

      <div class="search-row">
        <DxTextBox
          v-model="searchText"
          :placeholder="$t('search')"
          mode="search"
          styling-mode="outlined"
          width="100%"
          value-change-event="input"
        />
      </div>
    </div>

    <div v-if="filteredParameterGroups.length">
      <div class="table-header offers-parameters-table-header">
        <div></div>
        <div>PNT</div>
        <div>{{ $t('ProductionCenter') }}</div>
        <div>{{ $t('agronomySample.summary.sampleType') }}</div>
        <div>{{ $t('netAmount') }}</div>
      </div>

      <div
        v-for="(group, index) in filteredParameterGroups"
        :key="group.key"
      >
        <div
          class="table-row offers-parameter-group-row"
          :class="{ 'disabled-row': !group.children.length }"
          @click="toggleGroup(index, group)"
        >
          <div class="expand-cell">
            <span
              class="expand-symbol"
              :class="{ disabled: !group.children.length }"
            >
              {{ expandedGroupIndex === index ? '-' : '+' }}
            </span>
          </div>
          <div>{{ group.parent.PNT }}</div>
          <div>{{ getProductionCenterName(group.parent) }}</div>
          <div>{{ group.parent.SubfamiliaTM }}</div>
          <div>{{ calculateNetAmount(group.parent) }}</div>
        </div>

        <div
          v-if="expandedGroupIndex === index && group.children.length"
          class="tableContainerSamples offers-parameters-child-container"
        >
          <div class="card-header-offers">
            {{ $t('alert.parameters') }}
          </div>

          <div
            class="table-header-sample offers-parameters-child-header"
          >
            <div>{{ $t('description') }}</div>
            <div>{{ $t('agronomySample.summary.sampleType') }}</div>
            <div>{{ $t('netAmount') }}</div>
          </div>

          <div
            v-for="(parameter, parameterIndex) in group.children"
            :key="parameterKey(parameter, parameterIndex)"
            class="table-row-sample offers-parameters-child-row"
          >
            <div>
              {{ parameter.ParametroTecnica || parameter.PNT }}
            </div>
            <div>{{ parameter.SubfamiliaTM }}</div>
            <div>{{ calculateNetAmount(parameter) }}</div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="offers-parameters-empty">
      {{ $t('noDataText') }}
    </div>
  </div>
</template>

<script>
import { DxTextBox } from 'devextreme-vue/text-box';
import { mapGetters } from 'vuex';
import helpers from '@/js/helpers';

export default {
  components: {
    DxTextBox,
  },
  props: {
    store: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      expandedGroupIndex: null,
      searchText: '',
    };
  },
  computed: {
    ...mapGetters('user', ['getLanguage']),
    ...mapGetters('contact', ['contactInfo']),
    normalizedQuery() {
      return (this.searchText || '').trim().toLowerCase();
    },
    parameterGroups() {
      const rows = Array.isArray(this.store) ? this.store : [];
      const parents = rows.filter(
        (row) => row.idPNT !== null && row.idPNT !== undefined
      );
      const children = rows.filter(
        (row) =>
          row.idPNT === null &&
          row.IdPnt !== null &&
          row.IdPnt !== undefined
      );

      if (!parents.length) {
        return rows.map((row, index) => ({
          key: this.parameterKey(row, index),
          parent: row,
          children: [],
        }));
      }

      return parents.map((parent, index) => ({
        key: this.parameterKey(parent, index),
        parent,
        children: children.filter(
          (child) => String(child.IdPnt) === String(parent.idPNT)
        ),
      }));
    },
    filteredParameterGroups() {
      const q = this.normalizedQuery;
      if (!q) return this.parameterGroups;

      return this.parameterGroups
        .map((group) => {
          const parentMatches = this.rowMatchesQuery(group.parent, q);
          const children = group.children.filter((child) =>
            this.rowMatchesQuery(child, q)
          );

          if (parentMatches) {
            return group;
          }

          if (children.length) {
            return { ...group, children };
          }

          return null;
        })
        .filter(Boolean);
    },
  },
  watch: {
    searchText() {
      const current =
        this.filteredParameterGroups[this.expandedGroupIndex];
      if (!current || !current.children.length) {
        this.expandedGroupIndex = null;
      }
    },
  },
  methods: {
    toggleGroup(index, group) {
      if (!group.children.length) return;
      this.expandedGroupIndex =
        this.expandedGroupIndex === index ? null : index;
    },
    parameterKey(row, fallback = '') {
      return [
        row.idPNT,
        row.IdPnt,
        row.PNT,
        row.ParametroTecnica,
        fallback,
      ]
        .filter((value) => value !== null && value !== undefined)
        .join('-');
    },
    getProductionCenterName(row) {
      return row.centroProduccion?.[0]?.Nombre || '';
    },
    rowMatchesQuery(row, query) {
      return [
        row.PNT,
        row.ParametroTecnica,
        this.getProductionCenterName(row),
        row.SubfamiliaTM,
        row.PrecioNetoCodDivisa,
        this.calculateNetAmount(row),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query);
    },
    formatAmount(amount, currency) {
      if (amount === null || amount === undefined || amount === '') {
        return '';
      }

      const parsedAmount = Number(
        typeof amount === 'string'
          ? amount.replace(',', '.').replace(/[^\d.-]/g, '')
          : amount
      );

      const locale = this.getLanguage || 'es';
      return helpers.formatCurrency(parsedAmount, currency, locale);
    },
    calculateNetAmount(row) {
      const rawValue = row.PrecioNetoCodDivisa || '';
      const [precioOriginal, moneda] = rawValue.split(' ');
      const precioBruto = Number(
        row.PrecioBruto || precioOriginal || 0
      );
      const dto = Number(row.Descuento || 0);
      let neto = precioBruto;
      if (dto !== 0) {
        neto = precioBruto - (precioBruto * dto) / 100;
      }
      return this.formatAmount(
        neto,
        this.getRowCurrency(row, moneda)
      );
    },
    getRowCurrency(row, moneda) {
      return helpers.resolveCurrency({
        conversionX3: row.ConversionX3,
        clientCurrency: this.contactInfo?.currency,
        additionalCurrencies: [row.CodDivisa],
        m: row.M,
        moneda,
        fallback: 'EUR',
      });
    },
    normalizeCurrency(currency) {
      return helpers.normalizeMonetaryUnit(currency);
    },
  },
};
</script>
<style scoped lang="scss">
.offer-parameters-details {
  padding: 0 10px 10px;
}

.offers-parameters-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 0 8px;
}

.offers-parameters-header .custom-card-header {
  margin: 0;
  padding: 0 !important;
}

.card-header-offers {
  color: #00346a !important;
  font-size: 17px !important;
  font-style: normal !important;
  font-weight: 500 !important;
  line-height: 120% !important;
  padding: 0 12px 12px !important;
}

.table-header,
.table-row {
  display: grid;
  grid-template-columns: 35px 1.3fr 1.8fr 1.4fr 1fr;
  padding: 12px;
  background: #f2f8e2;
  border-radius: 10px;
  align-items: center;
  color: #3e3c44;
  margin: 4px 0;
  gap: 8px;
}

.table-header {
  font-weight: 600;
  background-color: #f7f7f8;
}

.table-row {
  border-top: 1px solid #eee;
  color: #00346a;
  cursor: pointer;
  transition: background-color 0.2s;
}

.table-row:hover {
  background-color: #ecf7cc;
}

.disabled-row {
  cursor: default;
}

.disabled-row:hover {
  background-color: #f2f8e2;
}

.expand-cell {
  font-weight: 600;
  text-align: center;
}

.expand-symbol {
  color: #00346a;
  cursor: pointer;
  display: inline-block;
  font-size: 18px;
  font-weight: 600;
  line-height: 1;
  user-select: none;
}

.expand-symbol.disabled {
  color: #bdbdbd;
  cursor: not-allowed;
}

.tableContainerSamples {
  padding: 20px;
  background-color: #fcfdf3;
  border-radius: 10px;
}

.table-header-sample,
.table-row-sample {
  display: grid;
  grid-template-columns: 2fr 1.2fr 1fr;
  padding: 10px 10px 10px 24px;
  border-radius: 10px;
  background: #ebf2fa;
  align-items: center;
  color: #3e3c44;
  margin: 4px 0;
  gap: 8px;
}

.table-header-sample {
  font-weight: 600;
  background-color: #efeff1;
}

.search-row {
  width: 200px;
  margin-left: auto;
}

.search-row :deep(.dx-texteditor) {
  width: 200px;
  height: 35px;
  min-height: 35px;
  border-radius: 999px;
}

.search-row :deep(.dx-texteditor-input) {
  font-size: 13px;
  padding-top: 6px;
  padding-bottom: 6px;
}

.search-row :deep(.dx-texteditor-buttons-container) {
  height: 32px;
}

.search-row :deep(.dx-icon) {
  font-size: 16px;
}

.search-row
  :deep(.dx-texteditor.dx-editor-outlined .dx-texteditor-input) {
  padding-left: 30px !important;
}

.search-row :deep(.dx-texteditor.dx-editor-outlined .dx-placeholder) {
  padding-left: 5px !important;
}

.offers-parameters-empty {
  color: #8a8693;
  padding: 16px 12px;
  text-align: center;
}
</style>
